/* eslint-disable import/no-unresolved */
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  OnboardingBodyLayout,
  CustomDropdown,
  SelectCountryDropdown,
  MAIN_ICON_COLORS,
} from 'assets/components';
import { isIOS } from 'react-device-detect';
import { OnboardingContext } from '../../../contexts/onboarding/context';
import { useHistoryState } from '../../../utilities/sharedHooks/history';
import {
  SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY,
  SETTINGS_TIER_STANDARD_ALL_KEY,
} from '../../../utilities/sharedQueries';
import { shouldAskTinCountry } from '../../../utilities/custodyProvider';
import { PARTIAL_SIGN_UP } from '../mutations';
import { showAlert } from '../../../utilities/notifications';
import { FLOWS, sendStats } from '../../../services/mixpanel';
import {
  MXP_ONBOARDING_SIGN_UP_ADDITIONAL_DETAILS_VISIT,
  MXP_ONBOARDING_SIGN_UP_PERSONAL_INFO_CONFIRM,
} from '../mixpanel-events';
import { PARTIAL_SIGN_UP_PHASES, US_COUNTRY_ALPHA } from '../constants';
import Input from '../../../components/Input';
import { handleFormBlur } from '../../../utilities/formsUtils';
import { getWordCount } from '../../../utilities/helpers';
import Icon from '../../../components/Icon';
import { ICONS } from '../../../components/Icon/constants';

const AdditionalPersonalDetails = () => {
  const { t: translate } = useTranslation();

  const { state } = useLocation();
  const history = useHistory();

  const selectDropDownFields = [
    {
      type: 'img',
      data: 'iso3166alpha2',
      class: 'col-1 text-left position-relative',
      img: {
        sourcePath: './images/flags/1x1/',
        ext: '.svg',
        class: 'rounded-circle position-absolute top-50 start-0 translate-middle-y',
      },
    },
    {
      type: 'text',
      data: 'displayName',
      class: 'col-11 ps-2 text-left',
    },
  ];

  const { state: onboardingState } = useContext(OnboardingContext);
  const allowPartialOnboarding = onboardingState?.mainSettings?.feature_enable_partial_registration;

  const [isSourceOfIncomeOpened, setIsSourceOfIncomeOpened] = useState(false);
  const [isBusinessSectorOpened, setIsBusinessSectorOpened] = useState(false);
  const [isEstimatedIncomeOpened, setIsEstimatedIncomeOpened] = useState(false);
  const [isOccupationOpened, setIsOccupationOpened] = useState(false);

  const [form, setForm] = useHistoryState('additionalPersonalDetailsForm', {
    tin: '',
    tin_country_id: state?.countryOfResidenceId || '',
    tin_country_label: state?.countryOfResidenceLabel || '',
    tin_country_iso3166alpha3: state?.countryOfResidenceAlpha || '',
    tin_country_iso3166alpha2: state?.countryOfResidenceAlpha2 || '',
    occupation: { key: '', description: '' },
    occupationOther: '',
    sourceOfIncome: { key: '', description: '' },
    sourceOfIncomeOther: '',
    businessSector: { key: '', description: '' },
    estimatedIncome: { key: '', description: '' },
    selected_country_of_income_earned_id: '',
    selected_country_of_income_earned_label: '',
    selected_country_of_income_earned_iso3166alpha3: '',
    selected_country_of_income_earned_iso3166alpha2: '',
    second_selected_country_of_income_earned_id: '',
    second_selected_country_of_income_earned_label: '',
    second_selected_country_of_income_earned_iso3166alpha3: '',
    second_selected_country_of_income_earned_iso3166alpha2: '',
    third_selected_country_of_income_earned_id: '',
    third_selected_country_of_income_earned_label: '',
    third_selected_country_of_income_earned_iso3166alpha3: '',
    third_selected_country_of_income_earned_iso3166alpha2: '',
  });

  const [additionalCountryOfIncomeEarned, setAdditionalCountryOfIncomeEarned] = useState(
    state?.additionalCountryOfIncomeEarned || 0
  );

  const { data: standards } = useQuery(SETTINGS_TIER_STANDARD_ALL_KEY);
  const { data: supportedCountries } = useQuery(SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY);

  const isTinCountryAsked = shouldAskTinCountry(
    supportedCountries,
    state?.countryOfResidenceAlpha,
    state?.countryOfResidenceAlpha === US_COUNTRY_ALPHA
  );
  const tinCountry = isTinCountryAsked ? form.tin_country_iso3166alpha3 : undefined;

  const { mutate: partialSignup, isLoading } = useMutation(PARTIAL_SIGN_UP, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        sendStats(MXP_ONBOARDING_SIGN_UP_PERSONAL_INFO_CONFIRM, {}, FLOWS.ONBOARDING);
        history.push(`address-information`, {
          ...form,
          ...state,
          sourceOfIncome: form?.sourceOfIncome?.key,
          businessSector: form?.businessSector?.key,
          estimatedIncome: form?.estimatedIncome?.key,
          partialSignupData: data,
        });
      }
    },
  });

  const addCountryOfIncomeEarned = () => {
    setAdditionalCountryOfIncomeEarned((prev) => prev + 1);
  };

  const removeCountryOfIncomeEarned = (number) => {
    setAdditionalCountryOfIncomeEarned((prev) => Math.max(0, prev - 1));
    if (number === 'second' && form.third_selected_country_of_income_earned_id) {
      setForm({
        ...form,
        [`${number}_selected_country_of_income_earned_id`]:
          form.third_selected_country_of_income_earned_id,
        [`${number}_selected_country_of_income_earned_label`]:
          form.third_selected_country_of_income_earned_label,
        [`${number}_selected_country_of_income_earned_iso3166alpha2`]:
          form.third_selected_country_of_income_earned_iso3166alpha2,
        [`${number}_selected_country_of_income_earned_iso3166alpha3`]:
          form.third_selected_country_of_income_earned_iso3166alpha3,
        [`third_selected_country_of_income_earned_id`]: '',
        [`third_selected_country_of_income_earned_label`]: '',
        [`third_selected_country_of_income_earned_iso3166alpha2`]: '',
        [`third_selected_country_of_income_earned_iso3166alpha3`]: '',
      });
    } else {
      setForm({
        ...form,
        [`${number}_selected_country_of_income_earned_id`]: '',
        [`${number}_selected_country_of_income_earned_label`]: '',
        [`${number}_selected_country_of_income_earned_iso3166alpha2`]: '',
        [`${number}_selected_country_of_income_earned_iso3166alpha3`]: '',
      });
    }
  };

  const sourceOfIncomeOptions = useMemo(() => {
    if (standards) {
      return standards?.filter((item) => item.risk_type_key === 'source_of_wealth');
    }
    return [];
  }, [standards]);

  const estimatedIncomeOptions = useMemo(() => {
    if (standards) {
      return standards?.filter((item) => item.risk_type_key === 'estimated_annual_income_band');
    }
    return [];
  }, [standards]);

  const occupationOptions = useMemo(() => {
    if (standards) {
      return standards?.filter((item) => item.risk_type_key === 'occupation');
    }
    return [];
  }, [standards]);

  const businessSectorOptinos = useMemo(() => {
    if (standards) {
      return standards?.filter((item) => item.risk_type_key === 'business_sector');
    }
    return [];
  }, [standards]);

  useEffect(() => {
    sendStats(MXP_ONBOARDING_SIGN_UP_ADDITIONAL_DETAILS_VISIT, {}, FLOWS.ONBOARDING);
  }, []);

  useEffect(() => {
    if (!state?.email && !state?.partialSignupData?.email) {
      history.push('/onboarding/signup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e, fieldName) => {
    if (!e) return;
    if (!e.target) {
      if (
        [
          'tin_country',
          'selected_country_of_income_earned',
          'second_selected_country_of_income_earned',
          'third_selected_country_of_income_earned',
        ].includes(fieldName)
      ) {
        setForm({
          ...form,
          [`${fieldName}_id`]: e.id,
          [`${fieldName}_label`]: e.displayName,
          [`${fieldName}_iso3166alpha2`]: e.iso3166alpha2,
          [`${fieldName}_iso3166alpha3`]: e.iso3166alpha3,
        });
      } else {
        setForm({ ...form, [fieldName]: e });
      }
    } else if (
      [
        'tin_country',
        'selected_country_of_income_earned',
        'second_selected_country_of_income_earned',
        'third_selected_country_of_income_earned',
      ].includes(fieldName)
    ) {
      setForm({
        ...form,
        [`${fieldName}_id`]: e.id,
        [`${fieldName}_label`]: e.displayName,
        [`${fieldName}_iso3166alpha2`]: e.iso3166alpha2,
        [`${fieldName}_iso3166alpha3`]: e.iso3166alpha3,
      });
    } else {
      const { name, value } = e.target;
      const valueWordsNumber = value?.length > 0 ? getWordCount(value) : 0;
      if (name === 'sourceOfIncomeOther' && valueWordsNumber > 31) {
        return;
      }
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    if (allowPartialOnboarding) {
      const payload = {
        id: state?.partialSignupData?.id,
        phase: PARTIAL_SIGN_UP_PHASES.ADDITIONAL_DETAILS,
        tin: form?.tin,
        tin_country: tinCountry,
        occupation: form?.occupation?.key,
        source_of_wealth: form?.sourceOfIncome?.key,
        business_sector: form?.businessSector?.key,
        estimated_annual_income: form?.estimatedIncome?.key,
        verification_id: state?.verification_id,
        selected_country_where_income_earned: form?.selected_country_of_income_earned_iso3166alpha3,
      };

      if (form?.occupation?.key === 'other_specify_occupation' && form.occupationOther) {
        payload.occupation_free_text = form.occupationOther;
      }

      if (form?.second_selected_country_of_income_earned_iso3166alpha3) {
        payload.second_selected_country_where_income_earned =
          form?.second_selected_country_of_income_earned_iso3166alpha3;
      }

      if (form?.third_selected_country_of_income_earned_iso3166alpha3) {
        payload.third_selected_country_where_income_earned =
          form?.third_selected_country_of_income_earned_iso3166alpha3;
      }

      if (
        form?.sourceOfIncome?.key === 'other_specify_source_of_wealth' &&
        form?.sourceOfIncomeOther
      ) {
        payload.source_of_wealth_free_text = form?.sourceOfIncomeOther;
      }

      partialSignup(payload);
    } else {
      sendStats(MXP_ONBOARDING_SIGN_UP_PERSONAL_INFO_CONFIRM, {}, FLOWS.ONBOARDING);
      history.push(`address-information`, {
        ...state,
        ...form,
        tin_country_iso3166alpha3: tinCountry,
        additionalCountryOfIncomeEarned,
        occupation: form?.occupation?.key,
        sourceOfIncome: form?.sourceOfIncome?.key,
        businessSector: form?.businessSector?.key,
        estimatedIncome: form?.estimatedIncome?.key,
      });
    }
  };

  const handleBlur = (e) => {
    handleFormBlur(e);
    if (!e || !e.target) return;

    e.target.closest('.form-group').className = e.target
      .closest('.form-group')
      .className.replace('has-focus', '');
  };

  const clearError = (e) => {
    e.target.className = e.target.className.replace('has-error', '');
    if (e.target.closest('.form-group').className.indexOf('has-focus') === -1) {
      e.target.closest('.form-group').className = `${
        e.target.closest('.form-group').className
      } has-focus`;
    }
  };

  const openDropdown = (type) => {
    if (type === 'sourceOfIncome') {
      setIsSourceOfIncomeOpened((prev) => !prev);
      setIsBusinessSectorOpened(false);
      setIsEstimatedIncomeOpened(false);
      setIsOccupationOpened(false);
    }
    if (type === 'businessSector') {
      setIsBusinessSectorOpened((prev) => !prev);
      setIsSourceOfIncomeOpened(false);
      setIsEstimatedIncomeOpened(false);
      setIsOccupationOpened(false);
    }
    if (type === 'estimatedIncome') {
      setIsEstimatedIncomeOpened((prev) => !prev);
      setIsSourceOfIncomeOpened(false);
      setIsBusinessSectorOpened(false);
      setIsOccupationOpened(false);
    }
    if (type === 'occupation') {
      setIsOccupationOpened((prev) => !prev);
      setIsSourceOfIncomeOpened(false);
      setIsBusinessSectorOpened(false);
      setIsEstimatedIncomeOpened(false);
    }
  };

  const disabledButton = useMemo(
    () =>
      !form.tin ||
      (isTinCountryAsked && !form.tin_country_iso3166alpha3) ||
      !form.occupation?.key ||
      !form.sourceOfIncome?.key ||
      !form.businessSector?.key ||
      !form.estimatedIncome?.key ||
      !form.selected_country_of_income_earned_id ||
      (form.occupation?.key === 'other_specify_occupation' && !form.occupationOther) ||
      (form.sourceOfIncome?.key === 'other_specify_source_of_wealth' &&
        !form.sourceOfIncomeOther) ||
      isLoading,
    [form, isLoading, isTinCountryAsked]
  );

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          loading={isLoading ? 'true' : 'false'}
          form="additional-details-form"
          disabled={disabledButton}>
          {translate('confirm_button_label')}
        </button>
      </div>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          loading={isLoading ? 'true' : 'false'}
          form="additional-details-form"
          disabled={disabledButton}>
          {translate('confirm_button_label')}
        </button>
      </div>
    </>
  );

  return (
    <OnboardingBodyLayout
      title={translate('additional_details_title_label')}
      renderButtons={renderButtons}>
      <form
        onSubmit={handleSubmitForm}
        id="additional-details-form"
        className="w-100 personal-info-custom d-flex flex-column">
        <Input
          containerClass="w-100"
          groupClassName={`m-0 ${isIOS ? 'ios' : ''}`}
          placeholder={translate('tin_input_label')}
          label={translate('tin_input_label')}
          errorLabel={translate('input_required_error_label')}
          className={`${isIOS ? 'ios' : ''}`}
          name="tin"
          value={form.tin}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={clearError}
          withFloatingLabel
          withErrorLabel
        />
        {isTinCountryAsked && (
          <div
            className="form-group my-0 position-relative d-flex flex-column gap-3"
            style={{ position: 'unset' }}>
            <SelectCountryDropdown
              name="tin_country"
              placeholder={translate('tin_country_input_label')}
              getOptionLabel={(option) => option.displayName}
              isOptionSelected={(option) => option.id === form.tin_country_id}
              value={form.tin_country_label}
              defaultValue={form.tin_country_iso3166alpha3}
              flagValue={form.tin_country_iso3166alpha2}
              onChange={(data, name) => handleChange(data, name)}
              handleBlur={handleBlur}
              clearError={clearError}
              onFocus={() => {
                setIsSourceOfIncomeOpened(false);
                setIsBusinessSectorOpened(false);
                setIsEstimatedIncomeOpened(false);
                setIsOccupationOpened(false);
              }}
              optionName="displayName"
              prefix=""
              fieldsInfo={selectDropDownFields}
              containerClass="w-100 position-absolute start-0 bg-light p-3 mt-2 rounded shadow-lg"
              dropDownParentClass=""
              withValueImageFlag
              useFilteredCountries
              showAllCountriesNoRestrictions
            />
          </div>
        )}
        <div className="form-group">
          <CustomDropdown
            name="occupation"
            placeholder={translate('occupation_input_label')}
            onChange={(e) => handleChange(e, 'occupation')}
            handleBlur={handleBlur}
            clearError={clearError}
            value={form.occupation}
            options={occupationOptions}
            isOpened={isOccupationOpened}
            setIsOpened={() => openDropdown('occupation')}
          />
        </div>
        {form.occupation?.key === 'other_specify_occupation' && (
          <Input
            containerClass="w-100"
            groupClassName={`m-0 ${isIOS ? 'ios' : ''}`}
            placeholder={translate('occupation_other_input_label')}
            label={translate('occupation_other_input_label')}
            errorLabel={translate('input_required_error_label')}
            className={`${isIOS ? 'ios' : ''}`}
            name="occupationOther"
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={clearError}
            value={form.occupationOther}
          />
        )}
        <div className="form-group" style={{ marginTop: 0 }}>
          <CustomDropdown
            name="sourceOfIncome"
            placeholder={translate('source_of_income_label')}
            onChange={(e) => handleChange(e, 'sourceOfIncome')}
            handleBlur={handleBlur}
            clearError={clearError}
            value={form.sourceOfIncome}
            options={sourceOfIncomeOptions}
            isOpened={isSourceOfIncomeOpened}
            setIsOpened={() => openDropdown('sourceOfIncome')}
          />
        </div>
        {form.sourceOfIncome?.key === 'other_specify_source_of_wealth' && (
          <Input
            containerClass="w-100"
            groupClassName={`m-0 ${isIOS ? 'ios' : ''}`}
            placeholder={translate('source_of_income_other_input_label')}
            label={translate('source_of_income_other_input_label')}
            errorLabel={translate('input_required_error_label')}
            className={`${isIOS ? 'ios' : ''}`}
            name="sourceOfIncomeOther"
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={clearError}
            value={form.sourceOfIncomeOther}
          />
        )}
        <div className="form-group" style={{ marginTop: 0 }}>
          <CustomDropdown
            name="estimatedIncome"
            placeholder={translate('estimated_income_input_label')}
            onChange={(e) => handleChange(e, 'estimatedIncome')}
            handleBlur={handleBlur}
            clearError={clearError}
            value={form.estimatedIncome}
            options={estimatedIncomeOptions}
            isOpened={isEstimatedIncomeOpened}
            setIsOpened={() => openDropdown('estimatedIncome')}
          />
        </div>
        <div className="form-group" style={{ marginTop: 0 }}>
          <CustomDropdown
            name="businessSector"
            placeholder={translate('business_sector_label')}
            onChange={(e) => handleChange(e, 'businessSector')}
            handleBlur={handleBlur}
            clearError={clearError}
            value={form.businessSector}
            options={businessSectorOptinos}
            isOpened={isBusinessSectorOpened}
            setIsOpened={() => openDropdown('businessSector')}
          />
        </div>
        <div
          className="form-group my-0 position-relative d-flex flex-column gap-3"
          style={{ position: 'unset' }}>
          <SelectCountryDropdown
            name="selected_country_of_income_earned"
            placeholder={translate('country_of_income_earned')}
            getOptionLabel={(option) => option.displayName}
            isOptionSelected={(option) => option.id === form.selected_country_of_income_earned_id}
            value={form.selected_country_of_income_earned_label}
            defaultValue={form.selected_country_of_income_earned_iso3166alpha3}
            flagValue={form.selected_country_of_income_earned_iso3166alpha2}
            onChange={(data, name) => handleChange(data, name)}
            handleBlur={handleBlur}
            clearError={clearError}
            onFocus={() => {
              setIsSourceOfIncomeOpened(false);
              setIsBusinessSectorOpened(false);
              setIsEstimatedIncomeOpened(false);
            }}
            optionName="displayName"
            prefix=""
            fieldsInfo={selectDropDownFields}
            containerClass="w-100 position-absolute start-0 bg-light p-3 mt-2 rounded shadow-lg"
            dropDownParentClass=""
            withValueImageFlag
            useFilteredCountries
            showAllCountriesNoRestrictions
          />
          {additionalCountryOfIncomeEarned === 0 && (
            <button
              onClick={() => {
                addCountryOfIncomeEarned();
              }}
              type="button"
              className="btn text-primary rounded-3 text-f6 m-0 p-0 text-nowrap text-btn-item border-0 d-flex align-items-center gap-2">
              <div style={{ width: '1.5rem', height: '1.5rem' }}>
                <Icon icon={ICONS.CIRCLE_PLUS} color={MAIN_ICON_COLORS.PRIMARY} />
              </div>
              <b>{translate('add_second_country_of_income_earned_btn_label')}</b>
            </button>
          )}
        </div>
        {Array(additionalCountryOfIncomeEarned)
          .fill(0)
          .map((item, index) => {
            const fieldName =
              index === 0
                ? 'second_selected_country_of_income_earned'
                : 'third_selected_country_of_income_earned';
            const id =
              index === 0
                ? form?.second_selected_country_of_income_earned_id
                : form?.third_selected_country_of_income_earned_id;
            const countryOfIncomeNumber = index === 0 ? 'second' : 'third';

            const filterCountryIds = [form.selected_country_of_income_earned_id];
            if (form.second_selected_country_of_income_earned_id) {
              filterCountryIds.push(form.second_selected_country_of_income_earned_id);
            }
            if (form.third_selected_country_of_income_earned_id) {
              filterCountryIds.push(form.third_selected_country_of_income_earned_id);
            }

            return (
              <div
                /* eslint-disable-next-line react/no-array-index-key */
                key={`additional-country-of-income-earned-${form[`${fieldName}_label`]}`}
                id={`additional-country-of-income-earned-${form[`${fieldName}_label`]}`}
                className="d-flex flex-column gap-4">
                <div className="d-flex gap-4 align-content-center">
                  <div
                    className="form-group position-relative d-flex flex-column flex-fill gap-3"
                    style={{ position: 'unset' }}>
                    <SelectCountryDropdown
                      name={fieldName}
                      placeholder={translate('country_of_income_earned')}
                      getOptionLabel={(option) => option.displayName}
                      isOptionSelected={(option) => option.id === id}
                      value={form[`${fieldName}_label`] || ''}
                      flagValue={form[`${fieldName}_iso3166alpha2`] || ''}
                      onChange={(data, name) => handleChange(data, name)}
                      handleBlur={handleBlur}
                      clearError={clearError}
                      filterCountryIds={filterCountryIds}
                      onFocus={() => {
                        setIsSourceOfIncomeOpened(false);
                        setIsBusinessSectorOpened(false);
                        setIsEstimatedIncomeOpened(false);
                      }}
                      optionName="displayName"
                      prefix=""
                      fieldsInfo={selectDropDownFields}
                      containerClass="w-100 position-absolute start-0 bg-light p-3 mt-2 rounded shadow-lg"
                      dropDownParentClass=""
                      withValueImageFlag
                      useFilteredCountries
                    />
                    {additionalCountryOfIncomeEarned === 1 && (
                      <button
                        onClick={() => {
                          addCountryOfIncomeEarned();
                        }}
                        type="button"
                        className="btn text-primary rounded-3 text-f6 m-0 p-0 text-nowrap text-btn-item border-0 d-flex align-items-center gap-2">
                        <div style={{ width: '1.5rem', height: '1.5rem' }}>
                          <Icon icon={ICONS.CIRCLE_PLUS} color={MAIN_ICON_COLORS.PRIMARY} />
                        </div>
                        <b>{translate('add_third_country_of_income_earned_btn_label')}</b>
                      </button>
                    )}
                  </div>
                  <div className="d-flex">
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <button
                      onClick={() => {
                        removeCountryOfIncomeEarned(countryOfIncomeNumber);
                      }}
                      type="button"
                      className="btn text-primary text-f6 m-0 p-0 text-nowrap text-btn-item border-0 gap-2">
                      <div style={{ width: '2rem', height: '2rem' }}>
                        <Icon icon={ICONS.CIRCLE_MINUS} color={MAIN_ICON_COLORS.DANGER} />
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </form>
    </OnboardingBodyLayout>
  );
};

export default AdditionalPersonalDetails;
